import { toast } from 'sonner';
import { createOAuthConnectLifecycle, openOAuthConnectPopup } from '../oauth-connect-lifecycle';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { Button } from '@deepguide-ai/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { IntegrationIcon } from '../icons';

// ---------------------------------------------------------------------------
// Config modal — shown only when the integration already exists (managing the
// connection). Initial "Connect Gusto" flows through `handleConnect`, which
// opens the OAuth popup and never hits this modal.
// ---------------------------------------------------------------------------

function GustoConfigModal({ open, onOpenChange, integration, onComplete, workspaceId }: ConfigModalProps) {
  const handleDisconnect = async () => {
    if (!integration) return;
    try {
      await trpcClient.integrations.deleteIntegration.mutate({ id: integration.id });
      toast.success('Gusto disconnected');
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      });
      onOpenChange(false);
      onComplete();
    } catch (error) {
      toast.error(`Failed to disconnect: ${error instanceof Error ? error.message : 'unknown'}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IntegrationIcon type="Gusto" />
            Gusto
          </DialogTitle>
          <DialogDescription>
            {integration
              ? 'Connected via OAuth. Compliance scans will collect HR lifecycle evidence from your Gusto workspace.'
              : 'Click Connect on the card to authorize Screenata to access your Gusto data.'}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {integration && (
            <Button variant="destructive" onClick={handleDisconnect}>
              Disconnect
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Gusto integration provider — user-delegated OAuth (authorization code).
 *
 * Flow mirrors SlackProvider / BitbucketProvider: the "Connect" button opens
 * a popup to Gusto's authorize endpoint; on success, the callback route
 * (`/auth-callback/Gusto`) posts back to this window and the Integration row
 * is upserted with a refresh_token by the server.
 *
 * Credentials (GUSTO_CLIENT_ID / GUSTO_CLIENT_SECRET) live in the server
 * environment — the end user never sees them.
 */
export const GustoProvider: IntegrationProvider = {
  id: 'Gusto',
  name: 'Gusto',
  description: 'Employee lifecycle, onboarding/offboarding evidence for user provisioning controls',
  Icon: () => <IntegrationIcon type="Gusto" />,
  ConfigModal: GustoConfigModal,

  handleConnect: async ({ workspaceId, redirectUrl, onSuccess, onFinish }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish);
    try {
      const finalRedirect =
        redirectUrl ?? `${import.meta.env.VITE_WEB_URL ?? window.location.origin}/auth-callback/Gusto`;

      const data = await trpcClient.integrations.initGustoAuth.mutate({
        workspaceId,
        redirectUrl: finalRedirect,
      });
      if (!data.authUrl) {
        toast.error('Failed to initialize Gusto OAuth');
        finish('error');
        return;
      }

      openOAuthConnectPopup({
        authUrl: data.authUrl, windowName: 'gusto-auth', provider: 'Gusto',
        onSuccess: result => {
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
          toast.success('Gusto connected');
          onSuccess?.(result);
        },
        onError: message => toast.error(message),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: finish,
      })
    } catch (error) {
      console.error('Error initializing Gusto auth:', error);
      toast.error(
        `Error initializing Gusto authentication: ${error instanceof Error ? error.message : 'unknown error'}`,
      );
      finish('error');
    }
  },

  onConnectComplete: async ({ code, state }: { code: string; state: string }) => {
    return await trpcClient.integrations.completeGustoAuth.mutate({ code, state });
  },

  getFeatures: () => [
    {
      id: 'hr_evidence',
      name: 'HR evidence',
      description: 'Collect employee lifecycle and onboarding/offboarding evidence',
      enabled: true,
    },
  ],

  syncData: async () => {},
};
